import { ArrowRight, Sparkles } from 'lucide-react'
import { Link } from 'react-router-dom'

import { AppShell } from '@/components/px/AppShell'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { DensityNote } from '@/components/px/DensityNote'
import { KpiCard } from '@/components/px/KpiCard'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { libraryNav } from '@/components/px/nav'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  BAR_BEFORE_LABEL,
  BAR_NOW_LABEL,
  BREADCRUMB,
  CONCLUSION,
  CONTEXT_PILL,
  CYCLE_LABEL,
  CYCLE_LEDE,
  CYCLE_LIST_LABEL,
  CYCLE_ROWS,
  DENSITY_NOTE,
  HEALTH_KPIS,
  KPI_SECTION_LABEL,
  MODULE_LABEL,
  NO_FIX_SURFACE_NOTE,
  PAGE_LEDE,
  PAGE_TITLE,
  PATTERNED_TAG,
  STALL_CAPTION,
  STALL_COLUMNS,
  STALL_LABEL,
  STALL_ROWS,
  TOTALS,
  TOTALS_LABEL,
  days,
  ownerTone,
} from '@/demo/content/portfolioHealth'
import { thinSpace } from '@/demo/data'
import { cn } from '@/lib/utils'

/**
 * Surface 2.2 — portfolio health. Two readings sit on one page: what phases
 * now cost (a measured before/after, so the "before" bar is muted and the
 * "now" bar is aubergine — a decided fact, never a proposal), and where the
 * days actually go when work stalls. The page's conclusion is computed from
 * the stall register rather than asserted beside it, so the mint panel can
 * never drift from the table above it.
 */

/**
 * One bar of the paired now/before rail. Decorative: the day count renders as
 * text beside it, so the bar is hidden from assistive tech rather than
 * announcing a percentage that contradicts the figure.
 */
function CycleBar({
  label,
  pct,
  value,
  emphasis,
}: {
  label: string
  pct: number
  value: string
  emphasis: 'now' | 'before'
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-12 shrink-0 text-xs text-muted-foreground">{label}</span>
      <Progress
        aria-hidden
        value={pct}
        className={cn(
          'h-2.5 max-w-lg bg-muted',
          emphasis === 'before' && '[&>*]:bg-muted-foreground/45'
        )}
      />
      <span className="v-figure w-12 shrink-0 text-right text-sm tabular-nums text-foreground">
        {value}
      </span>
    </div>
  )
}

export default function PortfolioHealthPage() {
  return (
    <AppShell moduleLabel={MODULE_LABEL} breadcrumb={BREADCRUMB} contextPill={CONTEXT_PILL} nav={libraryNav}>
      <header className="max-w-3xl">
        <div className="flex flex-wrap items-center gap-3">
                  <h1 className="font-serif text-2xl font-semibold text-foreground">{PAGE_TITLE}</h1>
          <ConceptBadge />
        </div>
        <p className="mt-1.5 text-sm text-muted-foreground">{PAGE_LEDE}</p>
      </header>

      <section className="mt-5" aria-label={KPI_SECTION_LABEL}>
        <SectionLabel>{KPI_SECTION_LABEL}</SectionLabel>
        <div className="mt-2 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {HEALTH_KPIS.map((kpi) => (
            <KpiCard key={kpi.key} label={kpi.label} figure={kpi.figure} qualifier={kpi.qualifier} />
          ))}
        </div>
      </section>

      <section className="mt-8" aria-label={CYCLE_LABEL}>
        <SectionLabel>{CYCLE_LABEL}</SectionLabel>
        <p className="mt-1.5 max-w-3xl text-sm text-muted-foreground">{CYCLE_LEDE}</p>
        <ul className="mt-3 rounded-lg border border-border bg-card px-5 py-1" aria-label={CYCLE_LIST_LABEL}>
          {CYCLE_ROWS.map((row) => (
            <li
              key={row.key}
              className="v-row grid grid-cols-[12rem_1fr_6rem] items-center gap-4 py-3.5"
            >
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground">{row.name}</span>
                  {row.patterned && (
                    <Badge variant="outline" className="border-mint/50 font-normal text-accent-foreground">
                      {PATTERNED_TAG}
                    </Badge>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">{row.subLabel}</div>
              </div>

              <div className="space-y-1.5">
                <CycleBar label={BAR_NOW_LABEL} pct={row.nowPct} value={days(row.now)} emphasis="now" />
                <CycleBar
                  label={BAR_BEFORE_LABEL}
                  pct={row.beforePct}
                  value={days(row.before)}
                  emphasis="before"
                />
              </div>

              <div className="text-right">
                <div className="v-figure text-sm font-medium tabular-nums text-foreground">
                  {row.dropLabel}
                </div>
                <div className="v-figure text-xs tabular-nums text-muted-foreground">{row.dropPctLabel}</div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-8" aria-label={STALL_LABEL}>
        <SectionLabel>{STALL_LABEL}</SectionLabel>
        <div className="mt-2 rounded-lg border border-border bg-card">
          <Table>
            <TableCaption className="sr-only">{STALL_CAPTION}</TableCaption>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {STALL_COLUMNS.map((column) => (
                  <TableHead
                    key={column.key}
                    scope="col"
                    className={cn(
                      'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground',
                      column.numeric && 'text-right'
                    )}
                  >
                    {column.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {STALL_ROWS.map((row) => {
                const clientOwned = row.owner === 'Client'
                return (
                  <TableRow key={row.id} className={cn(clientOwned && 'bg-review-bg/60')}>
                    <TableCell
                      className={cn(
                        'max-w-md py-3 whitespace-normal border-l-[3px]',
                        clientOwned ? 'border-l-review/60' : 'border-l-transparent'
                      )}
                    >
                      {row.fix ? (
                        <Link
                          to={row.fix.to}
                          title={row.fix.label}
                          className="font-medium text-foreground underline-offset-4 hover:underline"
                        >
                          {row.reason}
                        </Link>
                      ) : (
                        <span className="font-medium text-foreground">{row.reason}</span>
                      )}
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {row.detail}
                        {!row.fix && ` · ${NO_FIX_SURFACE_NOTE}`}
                      </div>
                    </TableCell>
                    <TableCell className="v-figure text-right tabular-nums text-foreground">
                      {thinSpace(row.occurrences)}
                    </TableCell>
                    <TableCell className="v-figure text-right tabular-nums text-ink-soft">
                      {days(row.avgDelayDays)}
                    </TableCell>
                    <TableCell className="v-figure text-right tabular-nums text-foreground">
                      {days(row.delayDays)}
                    </TableCell>
                    <TableCell>
                      <StatusPill tone={ownerTone[row.owner]}>{row.owner}</StatusPill>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
            <TableFooter className="bg-muted/50">
              <TableRow className="hover:bg-transparent">
                <TableCell className="py-3 font-medium text-foreground">{TOTALS_LABEL}</TableCell>
                <TableCell className="v-figure text-right tabular-nums text-foreground">
                  {thinSpace(TOTALS.occurrences)}
                </TableCell>
                <TableCell />
                <TableCell className="v-figure text-right tabular-nums text-foreground">
                  {days(TOTALS.delayDays)}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          </Table>
        </div>
      </section>

      <section
        className="mt-8 rounded-lg border border-mint/50 bg-accent px-5 py-4"
        aria-label={CONCLUSION.headline}
      >
        <div className="flex items-start gap-3">
          <Sparkles className="mt-1 h-5 w-5 shrink-0 text-accent-foreground" aria-hidden />
          <div>
            <h2 className="font-serif text-xl font-semibold text-accent-foreground">
              {CONCLUSION.headline}
            </h2>
            <p className="v-figure mt-1 text-sm tabular-nums text-ink-soft">{CONCLUSION.arithmetic}</p>
            <p className="mt-3 max-w-3xl text-sm text-foreground">{CONCLUSION.insight}</p>
            <p className="mt-2 text-sm text-ink-soft">
              {CONCLUSION.cta.before}
              <Link
                to={CONCLUSION.cta.link.to}
                className="inline-flex items-center gap-1 font-medium text-accent-foreground underline underline-offset-4"
              >
                {CONCLUSION.cta.link.label}
                <ArrowRight className="h-3.5 w-3.5" aria-hidden />
              </Link>
            </p>
          </div>
        </div>
      </section>

      <DensityNote>
        {DENSITY_NOTE.before}
        <Link to={DENSITY_NOTE.link.to} className="text-accent-foreground underline underline-offset-4">
          {DENSITY_NOTE.link.label}
        </Link>
        {DENSITY_NOTE.after}
      </DensityNote>
    </AppShell>
  )
}
